import bufferpack from "bufferpack";
import { StringDecoder } from "string_decoder";
import { Buffer } from "buffer";
import getArrayBufferFromFile from "./utils/getArrayBufferFromFile";
import createInitialSequence from "./utils/createInitialSequence";
import validateSequenceArray from "./utils/validateSequenceArray";
import flattenSequenceArray from "./utils/flattenSequenceArray";
import { get } from "lodash-es";
import { XMLParser } from "fast-xml-parser";
import extractFileExtension from "./utils/extractFileExtension";
async function snapgeneToJson(fileObj, options = {}) {
  try {
    let read = function(size, fmt) {
      const buffer = Buffer.from(arrayBuffer.slice(offset, size + offset));
      offset += size;
      if (fmt) {
        const decoder = new StringDecoder(fmt);
        const toRet = decoder.write(buffer);
        return toRet;
      } else {
        return buffer;
      }
    };
    const returnVal = createInitialSequence(options);
    const arrayBuffer = await getArrayBufferFromFile(fileObj);
    const ext = extractFileExtension(options.fileName);
    let isProtein = options.isProtein;
    if (ext && /^(prot)$/.test(ext)) {
      isProtein = true;
      options.isProtein = true;
    }
    let offset = 0;
    async function unpack(size, mode) {
      const buffer = await read(size);
      const unpacked = await bufferpack.unpack(">" + mode, buffer);
      if (unpacked === void 0) return void 0;
      return await unpacked[0];
    }
    await read(1);
    const length = await unpack(4, "I");
    const title = await read(8, "ascii");
    if (length !== 14 || title !== "SnapGene") {
      throw new Error("Wrong format for a SnapGene file !");
    }
    const data = await {
      ...returnVal.parsedSequence,
      isProtein,
      isDNA: !!await unpack(2, "H") && !isProtein,
      exportVersion: await unpack(2, "H"),
      importVersion: await unpack(2, "H"),
      features: []
    };
    while (offset <= arrayBuffer.byteLength) {
      const next_byte = await read(1);
      const block_size = await unpack(4, "I");
      if (ord(next_byte) === 21 || ord(next_byte) === 0) {
        const props = await unpack(1, "b");
        const binaryRep = dec2bin(props);
        data.circular = isFirstBitA1(binaryRep);
        const size = block_size - 1;
        if (size < 0) return;
        data.size = isProtein ? size * 3 : size;
        data.sequence = await read(size, "utf8");
      } else if (ord(next_byte) === 10) {
        const strand_dict = {
          // [strand, arrowheadType]
          0: [1, "NONE"],
          // non-directional feature (in that case, the attribute is generally absent altogether)
          1: [1, "TOP"],
          // forward strand
          2: [-1, "BOTTOM"],
          // reverse strand
          3: [1, "BOTH"]
          // bi-directional feature
        };
        const xml = await read(block_size, "utf8");
        const b = new XMLParser({
          ignoreAttributes: false,
          attributeNamePrefix: "",
          isArray: (name) => name === "Feature" || name === "Segment"
        }).parse(xml);
        const { Features: { Feature = [] } = {} } = b;
        data.features = [];
        Feature.forEach((feat) => {
          const { directionality, Segment = [], name, type } = feat;
          let maxStart = 0;
          let maxEnd = 0;
          const locations = Segment && Segment.map((seg) => {
            if (!seg) throw new Error("invalid feature definition");
            const { range } = seg;
            let { start, end } = getStartAndEndFromRangeString(range);
            start = isProtein ? start * 3 : start;
            end = isProtein ? end * 3 + 2 : end;
            maxStart = Math.max(maxStart, start);
            maxEnd = Math.max(maxEnd, end);
            return {
              start,
              end
            };
          });
          data.features.push({
            name,
            type,
            ...locations?.length > 1 && { locations },
            strand: directionality ? strand_dict[directionality][0] : 1,
            arrowheadType: directionality ? strand_dict[directionality][1] : "NONE",
            start: maxStart,
            end: maxEnd
            // color,
          });
        });
      } else if (ord(next_byte) === 6) {
        const xml = await read(block_size, "utf8");
        const b = new XMLParser({}).parse(xml);
        const name = get(b, "Notes.CustomMapLabel");
        if (name) {
          data.name = name;
        }
        const description = get(b, "Notes.Description");
        if (description && typeof description === "string") {
          data.description = description.replace("<html><body>", "").replace("</body></html>", "");
        }
      } else {
        await read(block_size);
      }
    }
    returnVal.parsedSequence = data;
    return validateSequenceArray(
      flattenSequenceArray([returnVal], options),
      options
    );
  } catch (e) {
    console.error("Error trying to parse file as snapgene:", e);
    return [
      {
        success: false,
        messages: ["Import Error: Invalid File"]
      }
    ];
  }
}
function getStartAndEndFromRangeString(rangestring) {
  const [start, end] = rangestring.split("-");
  return {
    start: start - 1,
    end: end - 1
  };
}
function ord(string) {
  const str = string + "";
  const code = str.charCodeAt(0);
  if (code >= 55296 && code <= 56319) {
    const hi = code;
    if (str.length === 1) {
      return code;
    }
    const low = str.charCodeAt(1);
    return (hi - 55296) * 1024 + (low - 56320) + 65536;
  }
  if (code >= 56320 && code <= 57343) {
    return code;
  }
  return code;
}
export default snapgeneToJson;
function dec2bin(dec) {
  return (dec >>> 0).toString(2);
}
function isFirstBitA1(num) {
  return Number(num.toString().split("").pop()) === 1;
}
